'use client';

import React, { useCallback, useEffect, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import Header from '@/components/common/Header';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import FilterBar, { Filters } from './FilterBar';
import IdeaCard, { IdeaBankItem } from './IdeaCard';
import DiagnoseModal from './DiagnoseModal';

const COUNTRY_MAP: Record<string, string> = {
  CL: 'Chile', MX: 'México', AR: 'Argentina', CO: 'Colombia',
  PE: 'Perú', UY: 'Uruguay', EC: 'Ecuador', ES: 'España', US: 'Estados Unidos',
};

function detectCountry(): string {
  if (typeof navigator === 'undefined') return 'Chile';
  const code = navigator.language.split('-')[1]?.toUpperCase() ?? '';
  return COUNTRY_MAP[code] ?? 'Chile';
}

const EMPTY_FILTERS: Filters = { sector: '', tipo: '', impacto: [], innovacion: '', modelo: '' };
const MAX_IDEAS = 100;

const BG =
  'linear-gradient(135deg, #0d1b3e 0%, #1a0a30 50%, #0d1b3e 100%)';
const CARD =
  'bg-white/5 backdrop-blur-md rounded-2xl border border-white/10 shadow-xl';

export default function GeneradorContent() {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { user, loading: authLoading } = useAuth();

  const [isHydrated, setIsHydrated] = useState(false);
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [ideas, setIdeas] = useState<IdeaBankItem[]>([]);
  const [tokenBalance, setTokenBalance] = useState<number | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isDiagnosing, setIsDiagnosing] = useState(false);
  const [selectedIdea, setSelectedIdea] = useState<IdeaBankItem | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [showTrimConfirm, setShowTrimConfirm] = useState(false);
  const [isTrimming, setIsTrimming] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(true);

  useEffect(() => { setIsHydrated(true); }, []);

  // Auth redirect
  useEffect(() => {
    if (authLoading) return;
    if (user) return;
    const query = searchParams.toString();
    const returnTo = `${pathname}${query ? `?${query}` : ''}`;
    router.replace(`/login?redirect=${encodeURIComponent(returnTo)}`);
  }, [authLoading, pathname, router, searchParams, user]);

  const getToken = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token ?? null;
  }, []);

  // Cargar saldo e ideas
  useEffect(() => {
    if (!user) return;
    (async () => {
      const [walletRes, ideasRes] = await Promise.all([
        supabase.from('token_wallets').select('balance').eq('user_id', user.id).single(),
        supabase.from('idea_bank').select('*').eq('user_id', user.id).order('created_at', { ascending: false }),
      ]);
      if (walletRes.data) setTokenBalance((walletRes.data as any).balance);
      if (ideasRes.data) setIdeas(ideasRes.data as IdeaBankItem[]);
    })();
  }, [user]);

  const handleGenerate = async () => {
    if (!user || isGenerating) return;
    setError(null);

    if (ideas.length >= MAX_IDEAS) {
      setShowTrimConfirm(true);
      return;
    }

    setIsGenerating(true);
    try {
      const token = await getToken();
      const res = await fetch('/api/generador/generate-ideas', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...filters, country: detectCountry() }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.error === 'limite_alcanzado') { setShowTrimConfirm(true); return; }
        throw new Error(data.error || 'Error al generar ideas');
      }
      setIdeas((prev) => [...(data.ideas as IdeaBankItem[]), ...prev]);
      if (data.tokens_cost !== undefined && tokenBalance !== null) {
        setTokenBalance(tokenBalance - data.tokens_cost);
      }
      setSuccessMsg(`✨ ${data.ideas.length} nuevas ideas generadas`);
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error inesperado');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleTrim = async () => {
    setIsTrimming(true);
    setError(null);
    try {
      const token = await getToken();
      const res = await fetch('/api/generador/trim-ideas', {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Error al limpiar ideas');
      // Recargar
      const { data: fresh } = await supabase
        .from('idea_bank').select('*').eq('user_id', user!.id).order('created_at', { ascending: false });
      if (fresh) setIdeas(fresh as IdeaBankItem[]);
      setShowTrimConfirm(false);
      setSuccessMsg(`🗑️ ${data.deleted} ideas eliminadas`);
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al limpiar');
    } finally {
      setIsTrimming(false);
    }
  };

  const handleModalClose = async (updatedTitle: string, updatedObjective: string) => {
    if (!selectedIdea) { setSelectedIdea(null); return; }
    const changed = updatedTitle !== selectedIdea.title || updatedObjective !== selectedIdea.objective;
    if (changed) {
      // Actualizar localmente
      setIdeas((prev) =>
        prev.map((i) => i.id === selectedIdea.id ? { ...i, title: updatedTitle, objective: updatedObjective } : i)
      );
      // Persistir
      const token = await getToken();
      await fetch('/api/generador/update-idea', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ idea_id: selectedIdea.id, title: updatedTitle, objective: updatedObjective }),
      });
    }
    setSelectedIdea(null);
  };

  const handleDiagnose = async (title: string, objective: string) => {
    if (!selectedIdea || isDiagnosing) return;
    setIsDiagnosing(true);
    setError(null);
    try {
      const token = await getToken();
      const res = await fetch('/api/generador/diagnose-idea', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ idea_id: selectedIdea.id, title, objective }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Error al diagnosticar');
      // Actualizar estado local
      setIdeas((prev) =>
        prev.map((i) => i.id === selectedIdea.id
          ? { ...i, title, objective, status: 'diagnosing' }
          : i)
      );
      if (data.new_token_balance !== undefined) setTokenBalance(data.new_token_balance);
      setSelectedIdea(null);
      router.push(`/a-ide-coach?sessionId=${encodeURIComponent(data.session_id)}&context=expert`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error inesperado');
    } finally {
      setIsDiagnosing(false);
    }
  };

  if (!isHydrated || authLoading) {
    return (
      <div className="min-h-screen" style={{ background: BG }}>
        <div className="max-w-5xl mx-auto pt-32 px-4">
          <div className="h-64 bg-white/5 animate-pulse rounded-2xl" />
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: BG }}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white" />
      </div>
    );
  }

  const hasFilters = Object.values(filters).some((v) => (Array.isArray(v) ? v.length > 0 : !!v));
  const atLimit = ideas.length >= MAX_IDEAS;

  return (
    <>
      <Header />
      <div className="min-h-screen pt-20 pb-16" style={{ background: BG }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 space-y-6">

          {/* Hero */}
          <div className="pt-8 pb-2">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-pink-500 flex items-center justify-center text-xl shadow-lg shadow-pink-500/20">
                💡
              </div>
              <div>
                <h1 className="text-white text-2xl font-bold leading-tight">Generador de Ideas</h1>
                <p className="text-white/50 text-sm">Filtra y genera ideas como lo haría una pequeña "Builderton"</p>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              {tokenBalance !== null && (
                <span className="text-white/60 text-xs px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
                  🪙 {tokenBalance} tokens
                </span>
              )}
              <span className="text-white/40 text-xs px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
                💡 {ideas.length} / {MAX_IDEAS} ideas
              </span>
            </div>
          </div>

          {/* Alertas */}
          {error && (
            <div className="bg-red-500/15 border border-red-400/25 rounded-xl px-4 py-3 text-red-300 text-sm flex items-start gap-2">
              <span className="flex-shrink-0 mt-0.5">⚠️</span>
              <span>{error}</span>
            </div>
          )}
          {successMsg && (
            <div className="bg-emerald-500/15 border border-emerald-400/25 rounded-xl px-4 py-3 text-emerald-300 text-sm">
              {successMsg}
            </div>
          )}

          {/* Límite */}
          {atLimit && (
            <div className="bg-amber-500/10 border border-amber-400/25 rounded-xl px-5 py-4 text-amber-200 text-sm flex items-start gap-3">
              <span className="text-xl flex-shrink-0">⚠️</span>
              <div>
                <p className="font-semibold mb-1">Has alcanzado el límite de {MAX_IDEAS} ideas</p>
                <p className="text-amber-300/70 text-xs mb-3">Para generar más, puedes eliminar aleatoriamente la mitad de las ideas pendientes.</p>
                <button
                  type="button"
                  onClick={() => setShowTrimConfirm(true)}
                  className="text-xs px-3 py-1.5 rounded-lg bg-amber-500/20 border border-amber-400/30 hover:bg-amber-500/30 transition-all"
                >
                  Liberar espacio
                </button>
              </div>
            </div>
          )}

          {/* Panel de filtros */}
          <div className={CARD}>
            <button
              type="button"
              onClick={() => setFiltersOpen(!filtersOpen)}
              className="w-full flex items-center justify-between px-6 py-4 text-left"
            >
              <div className="flex items-center gap-2">
                <span className="text-white font-semibold text-sm">Filtros</span>
                {hasFilters && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-orange-500/20 border border-orange-400/30 text-orange-300">
                    Aplicados
                  </span>
                )}
              </div>
              <span className={`text-white/40 text-sm transition-transform duration-200 ${filtersOpen ? 'rotate-180' : ''}`}>
                ▼
              </span>
            </button>

            {filtersOpen && (
              <div className="px-6 pb-6 pt-1 border-t border-white/5 space-y-6">
                <FilterBar filters={filters} onChange={setFilters} />

                <div className="flex items-center gap-3 pt-2">
                  <button
                    type="button"
                    onClick={handleGenerate}
                    disabled={isGenerating || (tokenBalance !== null && tokenBalance < 1)}
                    className="flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-orange-500 to-pink-500 text-white font-semibold text-sm hover:opacity-90 transition-all disabled:opacity-40 disabled:cursor-not-allowed shadow-lg shadow-pink-500/20"
                  >
                    {isGenerating ? (
                      <>
                        <span className="animate-spin">⟳</span>
                        <span>Generando…</span>
                      </>
                    ) : (
                      <>
                        <span>✨</span>
                        <span>Generar 10 ideas</span>
                        <span className="text-white/60 text-xs">(1 token)</span>
                      </>
                    )}
                  </button>
                  {hasFilters && (
                    <button
                      type="button"
                      onClick={() => setFilters(EMPTY_FILTERS)}
                      className="text-white/40 hover:text-white/70 text-xs underline transition-colors"
                    >
                      Limpiar filtros
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Ideas */}
          {ideas.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-white/70 text-sm font-semibold uppercase tracking-widest">
                  Tus ideas · más recientes primero
                </h2>
                <span className="text-white/30 text-xs">{ideas.length} ideas</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {ideas.map((idea, idx) => (
                  <IdeaCard
                    key={idea.id}
                    idea={idea}
                    index={idx}
                    onClick={setSelectedIdea}
                  />
                ))}
              </div>
            </div>
          )}

          {ideas.length === 0 && !isGenerating && (
            <div className={`${CARD} p-12 text-center`}>
              <div className="text-5xl mb-4">💡</div>
              <p className="text-white/60 text-base mb-1">Aún no tienes ideas generadas</p>
              <p className="text-white/30 text-sm">Selecciona filtros y presiona <strong className="text-white/50">Generar 10 ideas</strong> para comenzar</p>
            </div>
          )}
        </div>
      </div>

      {/* Modal de diagnóstico */}
      {selectedIdea && (
        <DiagnoseModal
          idea={selectedIdea}
          tokenBalance={tokenBalance}
          onClose={handleModalClose}
          onDiagnose={handleDiagnose}
          isDiagnosing={isDiagnosing}
        />
      )}

      {/* Modal confirmación trim */}
      {showTrimConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="bg-[#1a0a30] border border-white/15 rounded-2xl p-8 max-w-sm w-full shadow-2xl text-center space-y-5">
            <div className="text-4xl">🗑️</div>
            <div>
              <h3 className="text-white font-bold text-lg mb-2">Liberar espacio</h3>
              <p className="text-white/60 text-sm">
                Se eliminarán aleatoriamente la mitad de tus ideas <strong className="text-white/80">pendientes</strong> para hacer espacio. Las ideas en diagnóstico o completadas no se verán afectadas.
              </p>
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowTrimConfirm(false)}
                className="flex-1 py-2.5 rounded-xl border border-white/15 text-white/70 hover:text-white text-sm transition-all"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleTrim}
                disabled={isTrimming}
                className="flex-1 py-2.5 rounded-xl bg-red-500/80 hover:bg-red-500 text-white font-semibold text-sm transition-all disabled:opacity-40"
              >
                {isTrimming ? 'Eliminando…' : 'Eliminar mitad'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
